/**
 * 2026-06-04 (Christian: "maybe the defects are not loaded?") — the analysis
 * panels ask for `pageSize=2000` ("the 709-row worst case fits in a single
 * response", R58-L-07 AC5) but the BE clamps list reads to the CONTRACT max
 * of 200 (`LIST_PAGE_SIZE_MAX` in lyse-analysis.service.ts, documented as
 * `pageSize<=200` on getDefects/getFindings). The clamp made the 2000-ask
 * silently TRUNCATE: items=200, total=709, no follow-up request — so the
 * filmstrip AI/defect badges and the All-images lists were missing ~70% of
 * the rows on finding-heavy DAs.
 *
 * This helper walks `page=1..n` (each at the contract max) until `total`
 * rows — or the caller's requested ceiling — are in hand, and returns ONE
 * stitched response so consumers stay shaped exactly like before.
 */

/** The lyse/v1 contract cap for getDefects / getFindings pageSize. */
export const LIST_PAGE_SIZE_MAX = 200;

interface PagedList<I> {
  items: I[];
  total: number;
}

export async function fetchAllListPages<I, R extends PagedList<I>>(
  fetchPage: (page: number, pageSize: number) => Promise<R>,
  /** The caller's requested pageSize (> LIST_PAGE_SIZE_MAX). */
  ceiling: number,
): Promise<R> {
  const first = await fetchPage(1, LIST_PAGE_SIZE_MAX);
  const items: I[] = [...first.items];
  let page = 1;
  while (items.length < Math.min(first.total, ceiling)) {
    page += 1;
    const next = await fetchPage(page, LIST_PAGE_SIZE_MAX);
    // Defensive: rows can vanish mid-walk (e.g. a concurrent dismiss makes
    // `total` stale) — an empty page means we're done regardless of total.
    if (next.items.length === 0) break;
    items.push(...next.items);
  }
  return {
    ...first,
    items: items.slice(0, ceiling),
    page: 1,
    pageSize: items.length,
    nextCursor: null,
  };
}
